import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import { sessionDeadlines, sessionPhase } from '../lib/sessionTiming';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

export default function SessionGuard({ user, setUser }) {
  const navigate = useNavigate();
  const [deadlines, setDeadlines] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const latest = useRef(null);
  const ended = useRef(false);
  const inFlight = useRef(false);
  const lastSent = useRef(0);
  const token = useRef(localStorage.getItem('token'));

  const end = useCallback((reason = 'Your session has ended. Please sign in again.') => {
    if (ended.current) return;
    ended.current = true;
    localStorage.removeItem('token');
    setUser(null);
    navigate('/auth', { replace: true });
    toast.info(reason);
  }, [navigate, setUser]);

  const refresh = useCallback(async (activity = false) => {
    if (ended.current || inFlight.current) return;
    inFlight.current = true;
    const startedAt = Date.now();
    const requestToken = localStorage.getItem('token');
    setBusy(true);
    try {
      const response = activity ? await axiosInstance.post('/auth/session/activity') : await axiosInstance.get('/auth/session');
      if (localStorage.getItem('token') !== requestToken) return;
      const next = sessionDeadlines(response.data, startedAt);
      latest.current = next;
      setDeadlines(next);
      setNow(Date.now());
      setProblem('');
      if (activity) lastSent.current = Date.now();
    } catch (error) {
      if (localStorage.getItem('token') !== requestToken) return;
      if (error.response?.status === 401 || sessionPhase(latest.current) === 'expired') end();
      else setProblem('Could not verify your session. Check your connection and try again.');
    } finally { inFlight.current = false; setBusy(false); }
  }, [end]);

  useEffect(() => {
    ended.current = false;
    latest.current = null;
    setDeadlines(null);
    token.current = localStorage.getItem('token');
    refresh();
    const interceptor = axiosInstance.interceptors.response.use(response => response, error => {
      const sentToken = error.config?.headers?.Authorization;
      if (error.response?.status === 401 && sentToken === `Bearer ${localStorage.getItem('token')}`) end();
      return Promise.reject(error);
    });
    const tick = () => {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) { end(); return; }
      if (currentToken !== token.current) {
        token.current = currentToken; latest.current = null; setDeadlines(null); refresh(); return;
      }
      setNow(Date.now());
      if (sessionPhase(latest.current) === 'expired') refresh();
    };
    const onActivity = (event) => {
      // Passive traffic, timers and scripted DOM events never renew the idle timer.
      if (!event.isTrusted || document.visibilityState !== 'visible' || sessionPhase(latest.current) !== 'active') return;
      if (Date.now() - lastSent.current >= 15000) refresh(true);
    };
    const onVisible = () => { tick(); if (document.visibilityState === 'visible') refresh(); };
    const onStorage = (event) => { if (event.key === 'token') onVisible(); };
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    events.forEach(name => window.addEventListener(name, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('storage', onStorage);
    const timer = setInterval(tick, 1000);
    // Status checks do not count as activity, including while another tab is active.
    const poll = setInterval(() => refresh(), 15000);
    return () => {
      clearInterval(timer); clearInterval(poll);
      events.forEach(name => window.removeEventListener(name, onActivity));
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('storage', onStorage);
      axiosInstance.interceptors.response.eject(interceptor);
    };
  }, [user.id, refresh, end]);

  const rawPhase = sessionPhase(deadlines, now);
  const phase = rawPhase === 'expired' ? 'checking' : rawPhase;
  const absoluteWarning = deadlines && deadlines.absolute - now <= deadlines.warning;
  const seconds = deadlines ? Math.max(0, Math.ceil((deadlines.expires - now) / 1000)) : 0;
  const signOut = () => { axiosInstance.post('/auth/logout').catch(() => {}); end('You have been signed out.'); };
  return <Dialog open={phase === 'checking' || phase === 'warning'}>
    <DialogContent onEscapeKeyDown={event => event.preventDefault()} onPointerDownOutside={event => event.preventDefault()} data-testid="session-warning" className="max-w-md [&>button]:hidden">
      <DialogHeader><DialogTitle>{phase === 'checking' ? 'Checking your session' : absoluteWarning ? 'Please sign in again soon' : 'Are you still there?'}</DialogTitle>
        <DialogDescription>{phase === 'checking' ? (problem || 'Please wait while Qflow checks your session.') : absoluteWarning ? 'Your total session limit is approaching. Staying active cannot extend it; sign in again to start a new session.' : 'You have been inactive. Choose Stay signed in to continue, or Qflow will sign you out. Unsaved work may be lost.'}</DialogDescription>
      </DialogHeader>
      {phase === 'warning' && <p role="timer" className="text-lg font-semibold">Signing out in {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}</p>}
      {problem && phase !== 'checking' && <p role="alert" className="text-sm text-red-600">{problem}</p>}
      <div className="flex justify-end gap-3">
        <button type="button" onClick={signOut} className="rounded-lg border px-3 py-2 text-sm">Sign out</button>
        {phase === 'checking' && problem && <button type="button" disabled={busy} onClick={() => refresh()} className="qflow-accent-bg rounded-lg px-3 py-2 text-sm">Try again</button>}
        {phase === 'warning' && (absoluteWarning ? <button type="button" autoFocus onClick={signOut} className="qflow-accent-bg rounded-lg px-3 py-2 text-sm">Sign in again</button> : <button type="button" autoFocus disabled={busy} onClick={() => refresh(true)} className="qflow-accent-bg rounded-lg px-3 py-2 text-sm" data-testid="stay-signed-in">{busy ? 'Checking…' : 'Stay signed in'}</button>)}
      </div>
    </DialogContent>
  </Dialog>;
}
